class WatchheadsMatchedExtras extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.config = JSON.parse(this.querySelector('[data-config]').textContent);
    this.dialog = this.querySelector('dialog');
    this.container = this.querySelector('[data-offers]');
    this.addButton = this.querySelector('[data-add]');
    this.error = this.querySelector('[role="status"]');
    this.skipButton = this.querySelector('.wh-extras__skip');
    this.confirmation = this.querySelector('.wh-extras__eyebrow');
    this.defaultSkipText = this.skipButton.textContent;
    this.defaultConfirmationText = this.confirmation.textContent;
    this.templates = [...this.querySelectorAll('template[data-offer]')];
    this.blackFriday = new Date().toISOString().slice(0, 10) === '2026-11-27';
    this.fraction = this.blackFriday ? 1 : 0.9;
    this.root = window.Shopify?.routes?.root || '/';
    this.listeners = new AbortController();
    document.addEventListener('cart:update', event => this.offer(event), { capture: true, signal: this.listeners.signal });
    document.addEventListener('click', event => this.checkout(event), { capture: true, signal: this.listeners.signal });
    this.querySelectorAll('[data-skip]').forEach(button => button.addEventListener('click', () => this.skip()));
    this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.skip(); });
    this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.skip(); });
    this.container.addEventListener('change', event => this.select(event));
    this.addButton.addEventListener('click', () => this.add());
  }
  disconnectedCallback() {
    this.listeners?.abort();
    this.ready = false;
  }
  signature(cart) {
    return cart.items.map(item => `${item.variant_id}:${item.quantity}`).sort().join('|');
  }
  async checkout(event) {
    const button = event.target instanceof Element ? event.target.closest('button[name=checkout], button#checkout, input[name=checkout]') : null;
    if (!button || button.disabled || this.allowCheckout || this.config.paused || !this.config.discountRulesReady || this.dataset.review === 'true') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.checkoutPending) return;
    this.checkoutPending = true;
    this.checkoutButton = button;
    try {
      const response = await fetch(this.root + 'cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error();
      const cart = await response.json();
      let declined = false;
      try { declined = sessionStorage.getItem('wh-extra-declined-v3') === this.signature(cart); } catch {}
      const source = cart.items.find(item => this.config.boxIds.includes(String(item.product_id))) || cart.items.find(item => this.config.categories[String(item.product_id)]);
      if (declined || !source || !this.offer({ detail: { data: { source: 'product-form-component', productId: String(source.product_id) }, resource: cart } })) this.resumeCheckout();
    } catch {
      this.resumeCheckout();
    }
  }
  resumeCheckout() {
    const button = this.checkoutButton;
    this.checkoutButton = null;
    this.checkoutPending = false;
    if (!button?.isConnected) return;
    this.allowCheckout = true;
    try { button.click(); } finally { this.allowCheckout = false; }
  }
  offer(event) {
    const detail = event.detail;
    const sourceId = String(detail?.data?.productId || '');
    if (detail?.data?.source !== 'product-form-component' || detail.data.didError || !this.config.categories[sourceId]) return;
    if ((this.config.paused || !this.config.discountRulesReady) && this.dataset.review !== 'true') return;
    const cart = detail.resource;
    if (!cart?.items?.length || this.dialog.open) return;
    const existingExtra = cart.items.some(item => (item.line_level_discount_allocations || []).some(allocation => allocation.discount_application?.title?.startsWith('Extra offer:')));
    const hasCombo = cart.items.some(item => String(item.variant_id) === '55320221122901') && cart.items.some(item => this.config.boxIds.includes(String(item.product_id)));
    if (existingExtra || hasCombo || (!this.blackFriday && this.basketRate(cart) > 0.1) || (this.blackFriday && this.basketRate(cart) !== 0.2)) return;
    const eligibleIds = new Set(['55341673742677','55341673775445','55341687669077','55341687701845','55341696778581','55627469848917','55341757464917','55341711720789','55341704905045']);
    const minimumExtraPrice = Math.max(0, ...cart.items.filter(item => eligibleIds.has(String(item.variant_id))).map(item => item.original_price));
    const order = {
      box: ['tech','glasses','passport','pen','desk','mouse'],
      watch: ['passport','tech','glasses','pen','desk','mouse'],
      travel: ['passport','tech','glasses','pen','desk','mouse'],
      desk: ['desk','mouse','tech','passport','glasses','pen'],
      tech: ['desk','mouse','pen','passport','glasses','tech'],
      eyewear: ['passport','tech','pen','desk','mouse','glasses'],
      carry: ['passport','tech','glasses','pen','desk','mouse']
    }[this.config.categories[sourceId]];
    const productIds = new Set(cart.items.map(item => String(item.product_id)));
    const hasTech = this.config.techIds.some(id => productIds.has(id));
    this.currency = cart.currency;
    this.container.replaceChildren();
    this.selection = null;
    this.addButton.disabled = true;
    this.error.hidden = true;
    for (const name of order) {
      if (this.container.children.length === 4) break;
      const template = this.templates.find(item => item.dataset.offer === name);
      if (!template || productIds.has(template.dataset.product)) continue;
      if (name === 'tech' && hasTech) continue;
      if (this.dataset.review !== 'true' && template.dataset.approved !== 'true') continue;
      const node = template.content.firstElementChild.cloneNode(true);
      const price = Number(template.dataset.price);
      if (price < minimumExtraPrice) continue;
      const fraction = this.fraction;
      node.dataset.expectedPrice = String(Math.round(price * fraction));
      node.dataset.offer = name;
      this.priceCard(node, price, cart.currency);
      node.querySelector('[data-original]').textContent = this.money(price, cart.currency);
      this.container.appendChild(node);
    }
    if (!this.container.children.length) return;
    this.drawer = document.querySelector('cart-drawer-component[auto-open]');
    this.drawer?.removeAttribute('auto-open');
    this.cartSignature = this.signature(cart);
    this.skipButton.textContent = this.checkoutButton ? this.config.messages.checkoutSkip : this.defaultSkipText;
    this.confirmation.textContent = this.checkoutButton ? this.config.messages.checkoutConfirmation : this.defaultConfirmationText;
    this.dialog.showModal();
    return true;
  }
  select(event) {
    const card = event.target.closest('[data-offer]');
    if (!card) return;
    const radio = card.querySelector('input[type="radio"]');
    const select = card.querySelector('select');
    if (event.target === select && select.value) {
      radio.checked = true;
      const option = select.selectedOptions[0];
      radio.value = option.value;
      card.dataset.expectedPrice = String(Math.round(Number(option.dataset.price) * this.fraction));
      this.priceCard(card, Number(option.dataset.price), this.currency);
      const image = card.querySelector('img');
      image.src = option.dataset.image;
      image.removeAttribute('srcset');
    }
    if (!radio.checked) return;
    this.selection = { id: Number(radio.value), price: Number(card.dataset.expectedPrice) };
    this.addButton.disabled = this.config.paused || !this.config.discountRulesReady || (select && !select.value) || this.dataset.review === 'true';
  }
  priceCard(card, retail, currency) {
    card.querySelector('[data-price]').textContent = this.money(Math.round(retail * (this.blackFriday ? 0.8 : 0.9)), currency);
    let note = card.querySelector('.wh-extras__basket-price');
    if (!note) { note = document.createElement('small'); note.className = 'wh-extras__basket-price'; card.querySelector('.wh-extras__price').after(note); }
    note.textContent = this.blackFriday ? this.config.messages.blackFriday : this.config.messages.withWh10.replace('__price__', this.money(Math.round(retail * 0.81), currency));
  }
  basketRate(cart) {
    return Math.max(0, ...(cart.cart_level_discount_applications || []).filter(discount => discount.value_type === 'percentage').map(discount => Number(discount.value) / 100));
  }
  money(amount, currency) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: currency || 'GBP' }).format(amount / 100);
  }
  skip() {
    this.dialog.close();
    try { sessionStorage.setItem('wh-extra-declined-v3', this.cartSignature || ''); } catch {}
    if (this.checkoutButton) { this.resumeCheckout(); return; }
    if (this.drawer) { this.drawer.setAttribute('auto-open', ''); this.drawer.open(); }
    else window.location.assign(this.root + 'cart');
  }
  async add() {
    if (this.config.paused || !this.config.discountRulesReady || this.dataset.review === 'true' || !this.selection) return;
    this.addButton.disabled = true;
    this.error.hidden = true;
    try {
      const beforeResponse = await fetch(this.root + 'cart.js', { headers: { Accept: 'application/json' } });
      if (!beforeResponse.ok) throw new Error(this.config.messages.error);
      const before = await beforeResponse.json();
      if (before.items.some(item => item.variant_id === this.selection.id)) { window.location.assign(this.root + 'cart'); return; }
      const response = await fetch(this.root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: this.selection.id, quantity: 1 }] }) });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || this.config.messages.error);
      const afterResponse = await fetch(this.root + 'cart.js', { headers: { Accept: 'application/json' } });
      if (!afterResponse.ok) throw new Error(this.config.messages.error);
      const after = await afterResponse.json();
      const expectedExtra = Math.round(this.selection.price * (1 - this.basketRate(before)));
      if (this.basketRate(after) < this.basketRate(before) || after.total_price > before.total_price + expectedExtra) {
        const added = after.items.find(item => item.variant_id === this.selection.id);
        if (added) await fetch(this.root + 'cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: added.key, quantity: 0 }) });
        throw new Error(this.config.messages.error);
      }
      window.location.assign(this.root + 'cart');
    } catch (problem) {
      this.error.textContent = problem.message || this.config.messages.error;
      this.error.hidden = false;
      this.addButton.disabled = false;
    }
  }
}
if (!customElements.get('watchheads-matched-extras')) customElements.define('watchheads-matched-extras', WatchheadsMatchedExtras);
